/**
 * Which site an estimate's per-line device picker should read from.
 *
 * Devices (assets) live on sites, so a line can only link one when the
 * estimate resolves to a site. Historically the picker read `estimate.site_id`
 * and nothing else — and three close-out/automation paths wrote estimates with
 * no `site_id` at all (Issue #1024). That combination left the office looking
 * at "Set a site to link a device" on a quote auto-built FROM a job that knows
 * its site perfectly well, with no way anywhere in the app to set one. The
 * device column was simply dead forever on those estimates.
 *
 * Those inserts now carry the site, but every estimate already written that way
 * still has NULL. Rather than backfill production data, the picker falls back
 * to the originating job's site. That is safe on the server side too:
 * `assetLinkableToEstimate` accepts any active device on any site owned by the
 * estimate's customer whenever the estimate itself has no site, and the job's
 * site belongs to the estimate's customer by construction — the POST/PATCH
 * estimate routes reject a job or site from a different customer.
 *
 * `source` tells the UI which of the two it got, so an inherited scope can say
 * so instead of silently pretending the estimate is site-scoped.
 */

export interface EstimateSiteScopeInput {
    site_id?: string | null;
    source_job_site_id?: string | null;
    source_job_site_name?: string | null;
}

export interface EstimateSiteScope {
    /** The site to load devices from — null when neither source has one. */
    siteId: string | null;
    /** "estimate" = the estimate's own site; "job" = inherited from its job. */
    source: "estimate" | "job" | "none";
    /** Site name, only known for the inherited case (the job join returns it). */
    inheritedSiteName: string | null;
}

export function estimateDeviceScope(est: EstimateSiteScopeInput | null | undefined): EstimateSiteScope {
    const own = est?.site_id ?? null;
    if (own) return { siteId: own, source: "estimate", inheritedSiteName: null };

    const fromJob = est?.source_job_site_id ?? null;
    if (fromJob) {
        return { siteId: fromJob, source: "job", inheritedSiteName: est?.source_job_site_name ?? null };
    }

    return { siteId: null, source: "none", inheritedSiteName: null };
}
